import React, { useState, useEffect } from 'react';
import { Shield, Lock, Eye, Zap, CheckCircle, ArrowRight, Globe, Server, Users, Award } from 'lucide-react';
import Header from "./header";
import Footer from "./footer";
const Cybersecurity = () => {
  const [isVisible, setIsVisible] = useState({});
  const [activeService, setActiveService] = useState(0);

  useEffect(() => {
    const observeElements = () => {
      const elements = document.querySelectorAll('.animate-on-scroll');
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setIsVisible(prev => ({
                ...prev,
                [entry.target.id]: true
              }));
            }
          });
        },
        { threshold: 0.1 }
      );

      elements.forEach((el) => observer.observe(el));
    };

    observeElements();
  }, []);

  const services = [
  {
  icon: Shield,
  title: "TOT Security ",
  description: "Comprehensive OT security solutions protecting industrial control systems, SCADA networks, and critical infrastructure from cyber threats.",
  features: ["ICS/SCADA protection", "Industrial network monitoring", "Critical infrastructure defense"]
},
{
  icon: Lock,
  title: "Physical Security Systems",
  description: "Integrated physical security solutions combining access control, surveillance, and perimeter protection for complete facility security.",
  features: ["Access control systems", "Video surveillance", "Perimeter security"]
},
    {
      icon: Eye,
      title: "Security Monitoring & Analytics",
      description: "24/7 security monitoring with advanced analytics to provide comprehensive visibility into your security posture.",
      features: ["24/7 SOC monitoring", "Advanced analytics", "Custom dashboards"]
    },
    {
      icon: Zap,
      title: "Incident Response & Recovery",
      description: "Rapid incident response and recovery services to minimize downtime and restore operations quickly.",
      features: ["Rapid response team", "Forensic analysis", "Recovery planning"]
    }
  ];

  const stats = [
    { icon: Shield, value: "99.9%", label: "Threat Detection Rate" },
    { icon: Users, value: "500+", label: "Protected Clients" },
    { icon: Globe, value: "24/7", label: "Global Monitoring" },
    { icon: Award, value: "ISO 27001", label: "Certified Security" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white relative overflow-hidden">
      {/* Animated Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-20 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-72 h-72 bg-cyan-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-20 left-40 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-2000"></div>
      </div>

        <Header />
<section className="relative px-6 py-20 text-center min-h-screen flex items-center">
  {/* Video Background */}
  <div className="absolute inset-0 w-full h-full">
    <video 
      className="w-full h-full object-cover"
      autoPlay 
      muted 
      loop 
      playsInline
    >
      <source src="./Animatecybersecurity.mp4" type="video/mp4" />
      {/* Fallback for browsers that don't support video */}
      <img src="security.jpg" alt="Security background" className="w-full h-full object-cover" />
    </video>
    {/* Dark overlay for better text readability */}
    <div className="absolute inset-0 bg-black bg-opacity-40"></div>
  </div>

  {/* Content */}
  <div className="relative z-10 max-w-4xl mx-auto">
    <div className="animate-on-scroll opacity-0 transition-all duration-1000 transform translate-y-10" 
         id="hero-title"
         style={{ opacity: isVisible['hero-title'] ? 1 : 0, transform: isVisible['hero-title'] ? 'translateY(0)' : 'translateY(40px)' }}>
     
      <p className="text-xl md:text-2xl mb-8 text-gray-300 leading-relaxed">
        Protect your digital assets with our comprehensive cybersecurity solutions. 
        Advanced threat detection, real-time monitoring, and expert response teams.
      </p>
    </div>
  </div>
</section>
      
   

  <section className="bg-white py-20 px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Privacy and <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">Security</span> Compliance
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Enterprise-grade security and compliance solutions designed to protect your business 
              and ensure regulatory adherence across all platforms and services.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-gray-50 p-8 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Data Protection</h3>
              <p className="text-gray-600">
                Advanced encryption and data protection measures to safeguard sensitive information.
              </p>
            </div>
            <div className="bg-gray-50 p-8 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Compliance Monitoring</h3>
              <p className="text-gray-600">
                Real-time monitoring and reporting for GDPR, CCPA, and other regulatory requirements.
              </p>
            </div>
            <div className="bg-gray-50 p-8 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Security Audits</h3>
              <p className="text-gray-600">
                Regular security assessments and vulnerability testing to maintain security standards.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Section 2: Light Services Section */}
    <section className="relative z-10 px-6 py-20">
        <div className="max-w-6xl mx-auto">
          <div className="animate-on-scroll opacity-0 transition-all duration-1000 transform translate-y-10 text-center mb-16"
               id="services-title"
               style={{ opacity: isVisible['services-title'] ? 1 : 0, transform: isVisible['services-title'] ? 'translateY(0)' : 'translateY(40px)' }}>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Our <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Services</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Comprehensive cybersecurity solutions designed to protect your business from evolving threats
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div key={index}
                   className="animate-on-scroll opacity-0 transition-all duration-1000 transform translate-y-10"
                   id={`service-${index}`}
                   style={{ 
                     opacity: isVisible[`service-${index}`] ? 1 : 0, 
                     transform: isVisible[`service-${index}`] ? 'translateY(0)' : 'translateY(40px)',
                     transitionDelay: `${index * 200}ms`
                   }}>
                <div className="relative group">
                  {/* Animated background behind card */}
                  <div className="absolute -inset-1 bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 animate-pulse"></div>
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl opacity-20 animate-pulse"></div>
                  
                  {/* Card content */}
                  <div className="relative bg-white/95 backdrop-blur-md rounded-2xl p-8 border border-gray-200 hover:border-cyan-300 hover:shadow-2xl hover:shadow-cyan-500/25 transition-all duration-500 transform hover:scale-105 group">
                    <div className="flex items-center mb-6">
                      <div className="bg-gradient-to-r from-cyan-500 to-blue-500 p-3 rounded-xl mr-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                        <service.icon className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-800">{service.title}</h3>
                    </div>
                    <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                    <ul className="space-y-2">
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center text-gray-700">
                          <CheckCircle className="w-5 h-5 text-cyan-500 mr-3 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

    

      {/* Section 4: Light Features Section */}
      <section className="bg-white text-gray-800 py-20 px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Comprehensive <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">Security</span> Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Built for modern enterprises with advanced security requirements
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">
                Enterprise-Grade Protection
              </h3>
              <ul className="space-y-4 text-gray-600">
                <li className="flex items-start">
                  <ArrowRight className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span>End-to-end encryption for all data transfers</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Multi-factor authentication and access controls</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span>Automated compliance reporting and documentation</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span>24/7 security monitoring and incident response</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-50 border border-gray-200 p-8 rounded-lg">
              <h4 className="text-xl font-semibold text-gray-900 mb-4">Security Stats</h4>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>Security Score</span>
                    <span>99.8%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-green-500 h-2 rounded-full" style={{width: '99.8%'}}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>Compliance Rate</span>
                    <span>100%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-500 h-2 rounded-full" style={{width: '100%'}}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>Uptime</span>
                    <span>99.9%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-purple-500 h-2 rounded-full" style={{width: '99.9%'}}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

     {/* Integration Section */}
       <section className="relative z-10 px-6 py-20 bg-black/20 backdrop-blur-md">
        <div className="max-w-6xl mx-auto">
          <div className="animate-on-scroll opacity-0 transition-all duration-1000 transform translate-y-10"
               id="integration-section"
               style={{ opacity: isVisible['integration-section'] ? 1 : 0, transform: isVisible['integration-section'] ? 'translateY(0)' : 'translateY(40px)' }}>
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold mb-6">
                  Seamless <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Integration</span>
                </h2>
                <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                  Our cybersecurity solutions integrate seamlessly with your existing infrastructure, 
                  providing comprehensive protection without disrupting your operations.
                </p>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <div className="bg-gradient-to-r from-cyan-500 to-blue-500 p-2 rounded-lg mr-4">
                      <Server className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-gray-300">Cloud & On-Premise Integration</span>
                  </div>
                  <div className="flex items-center">
                    <div className="bg-gradient-to-r from-cyan-500 to-blue-500 p-2 rounded-lg mr-4">
                      <Globe className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-gray-300">Multi-Platform Support</span>
                  </div>
                  <div className="flex items-center">
                    <div className="bg-gradient-to-r from-cyan-500 to-blue-500 p-2 rounded-lg mr-4">
                      <Zap className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-gray-300">Real-Time Deployment</span>
                  </div>
                </div>
              </div>
                    <div className="relative bg-white/90 backdrop-blur-md rounded-2xl p-8 border border-gray-200 shadow-2xl">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gradient-to-br from-cyan-50 to-blue-50 hover:from-cyan-100 hover:to-blue-100 rounded-xl p-6 text-center transition-all duration-300 transform hover:scale-105 group border border-cyan-200">
                      <div className="bg-gradient-to-r from-cyan-500 to-blue-500 p-3 rounded-xl inline-block mb-3 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                        <Shield className="w-8 h-8 text-white" />
                      </div>
                      <div className="text-sm font-semibold text-gray-700">Protection Layer</div>
                    </div>
                    <div className="bg-gradient-to-br from-blue-50 to-purple-50 hover:from-blue-100 hover:to-purple-100 rounded-xl p-6 text-center transition-all duration-300 transform hover:scale-105 group border border-blue-200">
                      <div className="bg-gradient-to-r from-blue-500 to-purple-500 p-3 rounded-xl inline-block mb-3 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                        <Eye className="w-8 h-8 text-white" />
                      </div>
                      <div className="text-sm font-semibold text-gray-700">Monitoring</div>
                    </div>
                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 rounded-xl p-6 text-center transition-all duration-300 transform hover:scale-105 group border border-purple-200">
                      <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-3 rounded-xl inline-block mb-3 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                        <Lock className="w-8 h-8 text-white" />
                      </div>
                      <div className="text-sm font-semibold text-gray-700">Encryption</div>
                    </div>
                    <div className="bg-gradient-to-br from-cyan-50 to-teal-50 hover:from-cyan-100 hover:to-teal-100 rounded-xl p-6 text-center transition-all duration-300 transform hover:scale-105 group border border-cyan-200">
                      <div className="bg-gradient-to-r from-cyan-500 to-teal-500 p-3 rounded-xl inline-block mb-3 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                        <Zap className="w-8 h-8 text-white" />
                      </div>
                      <div className="text-sm font-semibold text-gray-700">Response</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
      
          
      </section>
      {/* Section 6: Light CTA Section */}
      <section className="bg-white text-gray-800 px-6 py-20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="animate-on-scroll opacity-0 transition-all duration-1000 transform translate-y-10"
               id="cta-section"
               style={{ opacity: isVisible['cta-section'] ? 1 : 0, transform: isVisible['cta-section'] ? 'translateY(0)' : 'translateY(40px)' }}>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              Ready to <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">Secure</span> Your Future?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Don't wait for a security breach to happen. Protect your business today with our comprehensive cybersecurity solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-gradient-to-r from-cyan-500 to-blue-500 px-8 py-4 rounded-full font-semibold text-lg text-white hover:from-cyan-600 hover:to-blue-600 transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2">
                Get Protected Now <Shield className="w-5 h-5" />
              </button>
              <button className="border-2 border-cyan-500 px-8 py-4 rounded-full font-semibold text-lg text-cyan-600 hover:bg-cyan-500 hover:text-white transition-all duration-300 transform hover:scale-105">
                Schedule Consultation
              </button>
            </div>
          </div>
        </div>
      </section> 
      <Footer />
      {/* Footer */}
      
    </div>
  );
};

export default Cybersecurity;
